import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import styled from 'styled-components'

import type { components } from '../../../constants/schema'

import { AlignCenterItem } from '../../../components/common'
import { Text } from '../../../components/text'
import { ProductLabel, StyledLabel } from '../../../components/label'
import { StyledProductCard } from '../../../components/card/ProductCard'

import { ReactComponent as TimerIcon } from '../../../assets/ico/subscription/timer.svg'

import { Color } from '../../../theme/color'

import 'react-lazy-load-image-component/src/effects/blur.css'

const WHITE_LIST = ['SALE_TO_BE_RELEASED', 'SALE_RELEASE']

const DateLabel = ({
  activeProductSale,
  overallStatus,
}: Pick<components['schemas']['ProductDetailDto'], 'activeProductSale' | 'overallStatus'>) => {
  const [currentDate, setCurrentDate] = useState<string>('')
  const [overallLabel, setOverallLabel] = useState<string>('')
  const [isFinish, setIsFinish] = useState<boolean>(false)

  const isSale = WHITE_LIST.includes(overallStatus)

  useEffect(() => {
    const date = () => {
      switch (overallStatus) {
        case 'SALE_TO_BE_RELEASED':
          return activeProductSale?.releaseDate
        case 'SALE_RELEASE':
          return activeProductSale?.untilDate
        default:
          return activeProductSale?.createdDate
      }
    }

    const label = () => {
      switch (overallStatus) {
        case 'SALE_TO_BE_RELEASED':
          return '청약 시작'
        case 'SALE_RELEASE':
          return '마감까지'
        default:
          return ''
      }
    }

    const countDate = setInterval(() => {
      const nowDate = dayjs()

      if ((overallStatus === 'SALE_RELEASE' || overallStatus === 'SALE_FINISH') && dayjs(date()).diff(nowDate) < 0) {
        clearInterval(countDate)
        setIsFinish(true)
        return
      }

      if (overallStatus === 'SALE_TO_BE_RELEASED' && dayjs(date()).diff(nowDate) < 0) {
        if (dayjs(activeProductSale?.untilDate).diff(nowDate) < 0) {
          clearInterval(countDate)
          setIsFinish(true)
          return
        }
        const day = dayjs(activeProductSale?.untilDate).diff(nowDate, 'day')
        const hour = dayjs(activeProductSale?.untilDate).diff(nowDate, 'hour') % 24
        const minute = dayjs(activeProductSale?.untilDate).diff(nowDate, 'minute') % 60
        const second = dayjs(activeProductSale?.untilDate).diff(nowDate, 'second') % 60
        setCurrentDate(`${day}일 ${hour}:${minute}:${second}`)
        setOverallLabel('마감까지')
        return
      }

      const day = dayjs(date()).diff(nowDate, 'day')
      const hour = dayjs(date()).diff(nowDate, 'hour') % 24
      const minute = dayjs(date()).diff(nowDate, 'minute') % 60
      const second = dayjs(date()).diff(nowDate, 'second') % 60
      setCurrentDate(`${day}일 ${hour}:${minute}:${second}`)
      setOverallLabel(label())
    }, 1000)

    return () => {
      clearInterval(countDate)
    }
  }, [overallStatus, activeProductSale])

  return (
    <ArticleInfo>
      {isFinish || (!isSale && overallStatus !== 'REGISTERED') ? (
        <Text size={16} weight={700} color="text_white">
          청약이 마감되었어요.
        </Text>
      ) : (
        <>
          <AlignCenterItem>
            <TimerIcon />
            <Text size={14} color="text_white">
              {overallLabel}
            </Text>
          </AlignCenterItem>
          <Text size={18} weight={600} color="text_white" block={true}>
            {currentDate}
          </Text>
        </>
      )}
    </ArticleInfo>
  )
}

export const ProductCard = ({
  id,
  imageUri,
  artist,
  name,
  overallStatus,
  overallStatusLabel,
  activeProductSale,
}: components['schemas']['ProductDto']) => {
  const isSale = WHITE_LIST.includes(overallStatus)

  const InnerContent = () => (
    <Container radius={17} isShadow={true}>
      <ProductLabel size="large" shape="semiRound" type={overallStatus} label={overallStatusLabel} />
      <BackgroundImage src={imageUri} alt={name} />
      <FooterContainer>
        <ProductInfo>
          <Text size={24} weight={600} block={true} color="text_white">
            {artist}
          </Text>
          <Text size={14} block={true} color="text_white">
            {name}
          </Text>
        </ProductInfo>
        <FooterCardBox>
          <DateLabel activeProductSale={activeProductSale} overallStatus={overallStatus} />
        </FooterCardBox>
      </FooterContainer>
    </Container>
  )

  if (isSale) {
    return (
      <Link to={`/market/product/${id}`}>
        <InnerContent />
      </Link>
    )
  }

  return <InnerContent />
}

const Container = styled(StyledProductCard)`
  display: flex;
  z-index: 1;
  border: 1px solid ${Color.line};
  ${StyledLabel} {
    position: absolute;
    top: 0;
    left: 0;
  }
`

const BackgroundImage = styled.img`
  position: absolute;
  z-index: -1;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const FooterContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 0 18px 18px;
`

const ProductInfo = styled.div`
  padding: 0 16px;
`

const FooterCardBox = styled.div`
  margin-top: 8px;
`

const ArticleInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.3);
  ${AlignCenterItem} {
    gap: 4px;
  }
`
